import Button from 'tailwind-components/Button/Button';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import style from '../DashboardCustomer.module.scss';
import { Filter } from 'lucide-react';
import { ClickAwayListener, FormHelperText, Popover } from '@mui/material';

function areArraysEqual(a, b) {
  if (a.length !== b.length) return false;

  const sortedA = [...a].sort();
  const sortedB = [...b].sort();

  return sortedA.every((val, index) => val === sortedB[index]);
}

function isDataEqual(data1, data2) {
  if (!data1 || !data2) return false;
  if (typeof data1 === 'string' && typeof data2 === 'string') {
    return data1.trim() === data2.trim();
  }
  if (Array.isArray(data1) && Array.isArray(data2)) {
    return areArraysEqual(data1, data2);
  }
  if (typeof data1 === 'object' && typeof data2 === 'object') {
    return JSON.stringify(data1) === JSON.stringify(data2);
  }
  return data1 === data2;
}

function isDataEmpty(data) {
  if (data == null) return true;
  if (typeof data === 'string') return data.trim() === '';
  if (Array.isArray(data)) return data.length === 0;
  if (typeof data === 'object') return Object.keys(data).length === 0;
  return false;
}

const HeaderFilterToolTip = ({
  clearFilters = () => {},
  applyFilters = () => {},
  children,
  selectedTags = null,
  initialData,
  forceUpdateProp = null,
  statusName = 'agent' // Default to 'agent', can be 'agent', 'AOR', or 'client'
}) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [snapshotData, setSnapshotData] = useState(initialData);
  const prevOpenRef = React.useRef(open);
  const initialDataRef = React.useRef(initialData);

  // Keep ref updated with latest initialData
  useEffect(() => {
    initialDataRef.current = initialData;
  }, [initialData]);

  // Update snapshot when popup opens (capture initial state)
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    if (open && !wasOpen) {
      // Popup just opened, capture snapshot from current initialData
      const currentData = initialDataRef.current;
      setSnapshotData(
        Array.isArray(currentData) ? [...currentData] : currentData
      );
    }
    prevOpenRef.current = open;
  }, [open]); // Only depend on open

  // Reset when forceUpdateProp changes (external update) but only if popup is closed
  useEffect(() => {
    if (forceUpdateProp && !open) {
      setSnapshotData(
        Array.isArray(initialData) ? [...initialData] : initialData
      );
    }
  }, [forceUpdateProp, initialData, open]);

  const hasUnsavedFilters =
    initialData && !isDataEqual(initialData, snapshotData);

  // Check if initialData is empty (no checkboxes selected)
  const isInitialDataEmpty = isDataEmpty(initialData);

  const handleTooltipOpen = e => {
    setAnchorEl(e.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    if (!open && !anchorEl) return;

    if (hasUnsavedFilters && !isInitialDataEmpty) {
      applyFilters();
      setSnapshotData(
        Array.isArray(initialData) ? [...initialData] : initialData
      );
    }
    setOpen(false);
    setAnchorEl(null);
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '5px',
            alignItems: 'center'
          }}
        >
          <Button
            size='small'
            variant={isDataEmpty(initialData) ? 'default' : 'primary'}
            onClick={handleTooltipOpen}
            icon={<Filter size={20} />}
          />
          {selectedTags}
        </div>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          style={{ marginTop: '5px' }}
        >
          {children}

          <div
            className={style.clearApplyFilters}
            style={{ padding: '16px 16px 16px 16px', flexDirection: 'column' }}
          >
            {isInitialDataEmpty && (
              <FormHelperText
                error
                sx={{ mb: 0.5, fontSize: '0.75rem', width: '100%' }}
              >
                {statusName === 'AOR'
                  ? 'An AOR status must be selected'
                  : statusName === 'client'
                  ? 'A Client status must be selected'
                  : 'An Agent status must be selected'}
              </FormHelperText>
            )}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '12px',
                justifyContent: 'center'
              }}
            >
              <Button
                text='Clear'
                size='medium'
                variant='secondary'
                isDisabled={isDataEmpty(initialData)}
                onClick={clearFilters}
              />

              <Button
                text='Apply Filter'
                size='medium'
                variant='primaryDarkened'
                isDisabled={!hasUnsavedFilters || isInitialDataEmpty}
                onClick={handleClose}
              />
            </div>
          </div>
        </Popover>
      </div>
    </ClickAwayListener>
  );
};

HeaderFilterToolTip.propTypes = {
  customOnClose: PropTypes.func,
  children: PropTypes.node,
  clearFilters: PropTypes.func,
  applyFilters: PropTypes.func,
  selectedTags: PropTypes.node,
  initialData: PropTypes.any,
  forceUpdateProp: PropTypes.any,
  statusName: PropTypes.string
};

export default HeaderFilterToolTip;
